import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Relation} from './relation';
import {BaseNumberApi, ListFilter, Pagination} from '@shared/base';
import {Contract} from '@app/contract/shared/contract';
import {Invoice} from '@app/invoice/shared/invoice';
import {Purchase} from '@app/purchase/shared/purchase';
import {ApiUtils} from '@shared/utils/api.utils';
import {Label} from '@shared/objects/label';
import {RelationArticle} from './relation-article';
import {Communication} from "@app/relation/shared/communication";
import {File as UfFile} from '@shared/components/files/file';

@Injectable({
  providedIn: 'root'
})
export class RelationApi extends BaseNumberApi<Relation> {

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient, 'relations');
  }

  create(): Relation {
    return new Relation();
  }

  /**
   * @deprecated please use findByNumber
   * @param id
   */
  public findById(id: number): Observable<Relation> {
    const listFilter = new ListFilter();
    listFilter['id'] = '' + id;

    return this.findByFilter(listFilter, new Pagination(1, 1, 'id', 'desc'))
      .pipe(
        map(value => {
          return value.data[0];
        })
      );
  }

  public getCommunications(uuid: string, types: string[]): Observable<Communication[]> {
    const params = new HttpParams({
      fromObject: {
        types: types
      }
    });
    return this.httpClient.get<Communication[]>(`${this.endpoint}/${uuid}/getcommunications`, {params});
  }

  public toggle(number: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this.endpoint}/${number}/toggle`);
  }

  public articles(number: number): Observable<RelationArticle[]> {
    return this.httpClient.get<RelationArticle[]>(`${this.endpoint}/${number}/articles`);
  }

  public contracts(number: number): Observable<Contract[]> {
    return this.httpClient.get<Contract[]>(`${this.endpoint}/${number}/contracts`);
  }

  public invoices(number: number): Observable<Invoice[]> {
    return this.httpClient.get<Invoice[]>(`${this.endpoint}/${number}/invoices`);
  }

  public purchases(number: number): Observable<Purchase[]> {
    return this.httpClient.get<Purchase[]>(`${this.endpoint}/${number}/purchases`);
  }

  public unpaid(number: number): Observable<Invoice[]> {
    return this.httpClient.get<Invoice[]>(`${this.endpoint}/${number}/unpaid`);
  }

  public unpaidReport(number: number) {
    this.httpClient.get(`${this.endpoint}/${number}/unpaid-report`, {
      responseType: 'blob',
      observe: 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).subscribe(response => {
      ApiUtils.saveToFileSystem(response);
      // this.toastrService.clear();
    }, response => {
      console.error(response);
    });
  }

  public export(report: string) {
    this.httpClient.get(`${this.endpoint}/export/${report}?active=true`, {
      responseType: 'blob',
      observe: 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).subscribe(response => {
      ApiUtils.saveToFileSystem(response);
      // this.toastrService.clear();
    }, response => {
      console.error(response);
    });
  }

  public overview(type: string, order: string) {
    this.httpClient.get(`${this.endpoint}/export/${type}`, {
      responseType: 'blob',
      observe: 'response',
      params: {order: order},
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).subscribe({
      next: response => {
        ApiUtils.saveToFileSystem(response);
      },
      error: response => {
        console.error(response);
      }
    });
  }

  public labels(relation: Relation): Observable<Label[]> {
    return this.httpClient.get<Label[]>(`${this.endpoint}/${relation.number}/labels`);
  }


  public toggleLabel(relation: Relation, label: string) {
    return this.httpClient.get<boolean>(`${this.endpoint}/${relation.number}/label/${label}`);
  }

  public exportXLSX() {
    this.httpClient.get(`${this.endpoint}/exportXLSX`, {
      responseType: 'blob',
      observe: 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).subscribe({
      next: response => {
        ApiUtils.saveToFileSystem(response);
      }, error: response => {
        console.error(response);
      }
    });
  }

  public importRelations(file: File) {
    const form = new FormData();
    form.append('file', file, file.name);

    return this.httpClient.post(`${this.endpoint}/importXLSX`, form);
  }

  public getFiles(uuid: string): Observable<UfFile[]> {
    return this.httpClient.get<UfFile[]>(`${this.endpoint}/${uuid}/files`);
  }

  public uploadFiles(uuid: string, file: File): Observable<UfFile> {
    const formData = new FormData();
    formData.append("file", file);

    return this.httpClient.post<UfFile>(`${this.endpoint}/${uuid}/files`, formData);
  }

  public deleteFile(uuid: string, fileUuid: string): Observable<UfFile> {
    return this.httpClient.delete<UfFile>(`${this.endpoint}/${uuid}/files/${fileUuid}`);
  }
}
